import React, { Suspense } from 'react';
import { initReactI18next } from 'react-i18next';
import environment from '@ktag/styleguide/src/javascripts/environment/environment';
import analytics from '@ktag/styleguide/src/javascripts/analytics/analytics';
import {
  i18n,
  WebWrapper,
  PageWrapper,
  Header,
  Footer,
} from '@ktag/styleguide/dist/react';
import Stylesheet from 'lib/Stylesheet/Stylesheet';
import WAPWrapper from 'components/WAPWrapper';
import environmentConfig from './config/environment';

const FOOTER_CONFIG = {
  label: 'Footer',
  config: {
    headline: 'Generalsekretariat',
    contact: {
      name: null,
      department: 'Staatskanzlei',
      role: 'Generalsekretariat',
      division: 'Wahlen und Abstimmungen',
      title: null,
      further: null,
      firstname: null,
      lastname: null,
      address: 'Regierungsgebäude',
      zip: '5001',
      locality: 'Aarau',
    },
    contactlinks: [
      {
        title: 'Telefon: +41628351210 ',
        text: 'Telefon: +41628351210 ',
        url: 'tel:+41628351210',
        type: 'tel',
      },
      {
        title: 'wahlbuero@ag.ch',
        text: 'wahlbuero@ag.ch',
        url: 'mailto:wahlbuero@ag.ch',
        type: 'email',
      },
      {
        title: 'Website',
        text: 'Website',
        url: 'https://www.ag.ch/de/aktuell/wahlen-abstimmungen',
        type: 'website',
      },
    ],
    sociallinks: [
      {
        title: 'Facebook',
        text: 'Facebook',
        url: 'https://www.facebook.com/kantonaargau',
        type: 'facebook',
      },
      {
        title: 'Twitter',
        text: 'Twitter',
        url: 'https://twitter.com/kantonaargau',
        type: 'twitter',
      },
      {
        title: 'Instagram',
        text: 'Instagram',
        url: 'https://instagram.com/kantonaargau',
        type: 'instagram',
      },
    ],
    map: {
      address: 'Regierungsgebäude',
      zip: '5001 ',
      locality: 'Aarau',
    },
  },
  imprintLinks: [
    {
      title: 'Kontakt',
      url: 'https://www.ag.ch/de/ueber-uns/kontakt',
    },
    {
      title: 'Impressum',
      url: 'https://www.ag.ch/de/ueber-uns/impressum',
    },
    {
      title: 'Rechtliche Hinweise',
      url: 'https://www.ag.ch/de/ueber-uns/rechtliche-hinweise',
    },
    {
      title: 'Barrierefreiheit',
      url: 'https://www.ag.ch/de/ueber-uns/barrierefreiheit',
    },
  ],
  metaLinks: [
    {
      title: 'Jobs & Karriere',
      text: 'Jobs & Karriere',
      url: 'https://www.ag.ch/de/ueber-uns/jobs-karriere',
    },
    {
      title: 'Medien',
      text: 'Medien',
      url: 'https://www.ag.ch/de/aktuell/medien',
    },
    {
      title: 'Aktuell',
      text: 'Aktuell',
      url: 'https://www.ag.ch/de/aktuell',
    },
    {
      title: 'Themen',
      text: 'Themen',
      url: 'https://www.ag.ch/de/themen',
    },
    {
      title: 'Dienstleistungen',
      text: 'Dienstleistungen',
      url: 'https://www.ag.ch/de/smartserviceportal#/',
    },
    {
      title: 'Behördenverzeichnis',
      text: 'Behördenverzeichnis',
      url: 'https://www.ag.ch/de/ueber-uns/kontakt/behoerdenverzeichnis',
    },
  ],
};

interface Props {
  rootElement?: HTMLElement;
  standalone?: boolean;
  apiUrl?: string;
  archiveApiUrl?: string;
  mapUrl?: string;
  translationApiUrl?: string | null;
  translationMissingApiUrl?: string | null;
  previewUser?: string;
  previewPassword?: string;
  navigationCurrent: string;
  navigationSource: string;
}

interface I18nProjectConfig {
  ns?: string[];
  backend?: { loadPath: string; addPath?: string };
  saveMissing?: boolean;
}

function App(props: Props) {
  const {
    rootElement,
    standalone,
    apiUrl,
    archiveApiUrl,
    mapUrl,
    translationApiUrl,
    translationMissingApiUrl,
    previewUser,
    previewPassword,
    navigationCurrent,
    navigationSource,
  } = props;

  // Initialize the environment/analytics with the web-config from @ktag/styleguide
  // if you do NOT pass an environmentConfig, the app will try to use the environment
  // config of the 'surrounding' environment, if it is e.g. embedded in www.ag.ch
  environment.initialize(environmentConfig);
  analytics.initialize();

  // initialize i18n and declare all known required namespaces
  // upfront to combine loading labels into a single request.
  const i18nProjectConfig: I18nProjectConfig = {
    ns: ['core', 'wap'],
  };
  if (translationApiUrl) {
    i18nProjectConfig.backend = { loadPath: translationApiUrl };
    if (translationMissingApiUrl) {
      i18nProjectConfig.saveMissing = true;
      i18nProjectConfig.backend.addPath = translationMissingApiUrl;
    }
  }
  i18n.initialize(i18nProjectConfig, {
    plugin: initReactI18next,
    allowMultiLoading: true,
  });

  // Production view.
  // Nothing but the actual app is rendered.
  if (standalone !== true) {
    return (
      <Suspense fallback={<div />}>
        <WebWrapper renderDocument resetCSS parentElement={rootElement}>
          <Stylesheet inShadowDOM />
          <WAPWrapper
            apiUrl={apiUrl}
            archiveApiUrl={archiveApiUrl}
            mapUrl={mapUrl}
            previewPassword={previewPassword}
            previewUser={previewUser}
            navigationCurrent={navigationCurrent}
            navigationSource={navigationSource}
          />
        </WebWrapper>
      </Suspense>
    );
  }

  // Develop and debug view
  // A complete web view is rendered around the app including
  // the loading of all necessary resources (css, images, fonts, icons, ...)
  return (
    <Suspense fallback={<div />}>
      <WebWrapper>
        <Stylesheet />
        <PageWrapper>
          <Header />
          <PageWrapper.Main>
            <WAPWrapper
              apiUrl={apiUrl}
              archiveApiUrl={archiveApiUrl}
              mapUrl={mapUrl}
              previewPassword={previewPassword}
              previewUser={previewUser}
              navigationCurrent={navigationCurrent}
              navigationSource={navigationSource}
            />
          </PageWrapper.Main>
          <Footer {...FOOTER_CONFIG} />
        </PageWrapper>
      </WebWrapper>
    </Suspense>
  );
}

App.defaultProps = {
  standalone: false,
  apiUrl: '',
  archiveApiUrl: '',
  mapUrl: 'https://www.ag.ch/app/svg-map/map?type=gemeinden&year=',
  translationApiUrl: null,
  translationMissingApiUrl: null,
};

export default App;
